import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";

export type BuilderSummary = FunctionReturnType<
  typeof api.products.categoryBuilderQueries.getBuilderSummary
>;
export type BuilderMatches = FunctionReturnType<
  typeof api.products.categoryBuilderQueries.matchListings
>;
export type BuilderListing = BuilderMatches["listings"][number];
export type HeightBucket = BuilderSummary["heights"][number]["bucket"];

export interface BuilderSpec {
  conditions: string[];
  footprint: string | null;
  heightBucket: HeightBucket | null;
  walls: number | null;
  specs: Record<string, string>;
}

export const SPEC_PARAM_PREFIX = "spec.";

export interface Dock {
  postalCode: string;
  lat?: number;
  lng?: number;
  state?: string;
  label: string;
}

export function dockCoordinates(dock: Dock | null): { latitude: number; longitude: number } | undefined {
  return dock?.lat !== undefined && dock.lng !== undefined
    ? { latitude: dock.lat, longitude: dock.lng }
    : undefined;
}

export const HEIGHT_LABELS: Record<HeightBucket, string> = {
  under36: 'Under 36"',
  "36to44": '36–44"',
  over44: 'Over 44"',
};

export const HEIGHT_ORDER: HeightBucket[] = ["under36", "36to44", "over44"];

export const EMPTY_SPEC: BuilderSpec = {
  conditions: [],
  footprint: null,
  heightBucket: null,
  walls: null,
  specs: {},
};

function isHeightBucket(value: string | undefined): value is HeightBucket {
  return value === "under36" || value === "36to44" || value === "over44";
}

export function specFromParams(
  params: Record<string, string | string[] | undefined>,
): BuilderSpec {
  const first = (key: string) => {
    const value = params[key];
    return Array.isArray(value) ? value[0] : value;
  };
  const walls = Number.parseInt(first("walls") ?? "", 10);
  const height = first("height");
  const specs = Object.fromEntries(
    Object.keys(params)
      .filter((key) => key.startsWith(SPEC_PARAM_PREFIX))
      .flatMap((key) => {
        const value = first(key)?.trim();
        return value ? [[key.slice(SPEC_PARAM_PREFIX.length), value]] : [];
      }),
  );
  return {
    conditions: (first("grade") ?? "").split(",").filter(Boolean),
    footprint: first("fp") ?? null,
    heightBucket: isHeightBucket(height) ? height : null,
    walls: Number.isNaN(walls) ? null : walls,
    specs,
  };
}

export function specToMatchArgs(productTypeSlug: string, spec: BuilderSpec) {
  return {
    productTypeSlug,
    conditions: spec.conditions.length > 0 ? spec.conditions : undefined,
    footprint: spec.footprint ?? undefined,
    heightBucket: spec.heightBucket ?? undefined,
    walls: spec.walls ?? undefined,
    specs: Object.keys(spec.specs).length > 0
      ? Object.entries(spec.specs).map(([field, value]) => ({ field, value }))
      : undefined,
  };
}

export function formatFootprint(value: string): string {
  const [length, width] = value.split("x");
  return `${length}" × ${width}"`;
}

export function quoteHref(
  productTypeSlug: string,
  quantity: number,
  dock: Dock | null,
  spec: BuilderSpec,
  picked: ReadonlyArray<Pick<BuilderListing, "_id" | "publicIdentifier">>,
): string {
  const params = new URLSearchParams({
    type: "buy",
    product: productTypeSlug,
    qty: String(quantity),
  });
  if (dock) {
    if (dock.state) params.set("state", dock.state);
    params.set("zip", dock.postalCode);
  }
  if (spec.conditions.length > 0) params.set("grade", spec.conditions.join(","));
  if (spec.footprint) params.set("fp", spec.footprint);
  if (spec.heightBucket) params.set("height", spec.heightBucket);
  if (spec.walls !== null) params.set("walls", String(spec.walls));
  for (const [field, value] of Object.entries(spec.specs)) {
    params.set(`${SPEC_PARAM_PREFIX}${field}`, value);
  }
  if (picked.length > 0) {
    params.set("listings", picked.map((listing) => listing.publicIdentifier ?? listing._id).join(","));
  }
  return `/request-quote?${params.toString()}`;
}
